@if (isUserMessage()) {
  <div class="flex gap-2">
    <div
      class="flex justify-center items-center w-6 h-6 mt-2 font-semibold text-xs rounded-full bg-yellow-500 uppercase text-neutral-900 flex-shrink-0 select-none"
    >
      {{ userInitials }}
    </div>
    <div
      class="bg-neutral-700 rounded-2xl text-sm text-white py-2 px-3 whitespace-pre-wrap hyphens-auto w-auto flex"
    >
      <ng-container *ngTemplateOutlet="msg; context: { $implicit: message().message }" />

      @if (images()) {
        <div class="flex flex-row gap-2">
          @for (image of images(); track $index) {
            <div class="w-[96px] h-[108px] overflow-hidden rounded-lg">
              <p-image
                [src]="image.src$ | async"
                [alt]="image.name"
                class="object-cover"
                width="96px"
                preview="true"
                height="108px"
                imageClass="min-w-[96px] w-auto min-h-[108px] h-auto object-contain"
              />
            </div>
          }
        </div>
      }

      @if (bpmnFiles()) {
        <div class="flex flex-row gap-2">
          @for (bpmn of bpmnFiles(); track $index) {
            <div class="w-[96px] h-[108px] overflow-hidden rounded-lg">
              <p-image
                [alt]="bpmn.name"
                src="./assets/bpmn_thumb_full.svg"
                width="96px"
                preview="true"
                height="108px"
                imageClass="min-w-[96px] w-auto min-h-[108px] h-auto object-contain"
              />
            </div>
          }
        </div>
      }
    </div>
    <div class="flex justify-center items-center">
      <button
        [disabled]="!isCopyMessageAllowed()"
        [pTooltip]="copyTextToChatTooltip()"
        (click)="copyToChatInputField()"
        class="w-7 h-7 flex justify-center items-center border border-neutral-700 rounded hover:bg-neutral-100"
        tooltipPosition="left"
      >
        <flx-icon
          [name]="copyTextToChatIcon()"
          class="text-neutral-400"
        ></flx-icon>
      </button>
    </div>
  </div>
} @else {
  <div class="flex gap-2">
    <div class="flex justify-center items-center w-6 h-6 rounded-full bg-neutral-900 flex-shrink-0">
      <flx-icon name="flowx-logo-white"></flx-icon>
    </div>
    <div
      class="min-w-0 flex flex-col flex-auto gap-2 text-sm text-white word-wrap whitespace-pre-wrap hyphens-auto overflow-hidden"
    >
      @if (showThinking()) {
        <p-panel
          [toggleable]="true"
          [collapsed]="false"
          class="bg-neutral-900 max-w-[350px]"
        >
          <ng-template pTemplate="header">
            <div class="flex gap-2">
              <i class="ph ph-lightbulb-filament text-yellow-400 text-sm"></i>
              <span class="font-semibold text-sm text-neutral-400">Thinking...</span>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <div
              #thinkingContainer
              class="text-neutral-300 text-sm max-w-[350px] max-h-[200px] overflow-y-auto"
            >
              {{ message().thinking }}
            </div>
          </ng-template>
        </p-panel>
      }

      @if (showDocuments()) {
        <div class="flex flex-col gap-2">
          @if (message().documents.message) {
            <div>{{ message().documents.message }}</div>
          }

          <div class="flex gap-2 flex-wrap">
            @for (document of documents(); track $index) {
              <button
                [disabled]="isDocumentDisabled()"
                (click)="onContextDocumentClick(document)"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs"
              >
                {{ document.name }}
              </button>
            }
          </div>
        </div>
      }
      <ng-container *ngTemplateOutlet="msg; context: { $implicit: message().message }" />

      @if (uiFlowPages().length > 1 && isMessageLast() && hasDraft()) {
        <div class="flex flex-col gap-2 mt-2">
          <div class="text-neutral-400 text-xs font-semibold">Switch page preview:</div>
          <div class="flex flex-wrap gap-1.5">
            @for (page of uiFlowPages(); track page.index) {
              <button
                (click)="onSelectPage(page.index)"
                [class]="selectedPageIndex() === page.index
                  ? 'px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500 text-black cursor-default'
                  : 'px-3 py-1.5 rounded-lg text-xs font-semibold bg-neutral-800 text-neutral-300 hover:bg-neutral-700 hover:text-white cursor-pointer transition-colors'"
              >
                {{ page.label }}
              </button>
            }
          </div>
        </div>
      }

      @if (generators().summary && isMessageLast()) {
        <div
          class="text-neutral-300 bg-neutral-900 rounded-lg px-4 py-2 text-xs italic max-h-[24rem] overflow-auto"
        >
          <p class="text-neutral-300 font-bold">Description:</p>
          <ng-container *ngTemplateOutlet="msg; context: { $implicit: generators().description }" />
        </div>
        <div
          class="text-neutral-300 bg-neutral-900 rounded-lg px-4 py-2 text-xs italic max-h-[24rem] overflow-auto"
        >
          <p class="text-neutral-300 font-bold">Summary:</p>
          <ng-container *ngTemplateOutlet="msg; context: { $implicit: generators().summary }" />
        </div>

        <div class="flex">
          <button
            (click)="generatePlanFromSummary()"
            class="py-2 px-3 h-8 font-normal items-center flex text-center bg-green-900 rounded-lg text-xs gap-2 hover:border-green-400 border-2 border-green-900 cursor-pointer text-green-400 transition-all"
          >
            <i class="ph ph-lightning text-base"></i>
            Generate a Plan
          </button>
        </div>

        @if (generators().items) {
          <div class="flex flex-col rounded-lg p-0 gap-2 text-xs">
            <div class="text-neutral-300">
              I found {{ generators().count }}
              <span class="font-bold text-white">{{ generators().label }}</span> that can be
              {{ generators().action }}d:
            </div>

            <div class="flex flex-col gap-1">
              @for (generator of generators().items; track $index) {
                <div class="flex flex-row bg-black/30 gap-1 p-2 rounded-lg">
                  <div class="text-white">{{ $index + 1 }}.</div>
                  <div class="flex flex-col gap-1 items-start">
                    <div class="text-white font-bold break-normal">{{ generator.name }}</div>
                    <div
                      #generatorDescription
                      [class.line-clamp-none]="generator.expandedDescription"
                      class="relative text-neutral-200 text-[10px] line-clamp-6 break-normal"
                    >
                      <span>{{ generator.description }}</span>

                      @if (isMultiLineOverflows(generatorDescription)) {
                        <button
                          (click)="generator.expandedDescription = true"
                          class="text-white font-semibold absolute bottom-0 right-0"
                        >
                          More
                        </button>
                      }
                    </div>
                    <button
                      [disabled]="isGenerateFromDocumentContextDisabled()"
                      (click)="generateFromDocument(generator)"
                      class="font-semibold text-yellow-500"
                    >
                      <span>Generate This</span>
                    </button>
                  </div>
                </div>
              }
            </div>

            @if (!isExpandedGenerators()) {
              <button (click)="showAllGenerators()">Show All ({{ generators().count }})</button>
            }
          </div>
        } @else {
          <div class="text-neutral-300 italic text-xs">
            I didn’t find any process, so I would need more details about what you want to generate.
            I will still take into account the information from the file.
          </div>
        }
        @if (generators().images?.length > 0) {
          <div class="flex flex-col rounded-lg gap-2 text-xs">
            <div class="text-neutral-300">
              I found {{ generators().images.length }}
              <span class="font-bold text-white">images</span> that can be used:
            </div>
            <p-carousel
              [value]="generators().images"
              [numVisible]="2"
              [numScroll]="2"
              [circular]="true"
              class="w-full text-neutral-300 bg-black/30 p-2 rounded-lg"
            >
              <ng-template
                let-image
                pTemplate="item"
              >
                <div
                  class="overflow-hidden rounded-lg max-h-64 max-w-64 w-auto h-auto text-neutral-300 hover:text-yellow-500 transition-all duration-300 hover:cursor-pointer"
                >
                  <img
                    [src]="image.src"
                    [alt]="image.name"
                    (click)="implementImage(image)"
                    class="w-48 h-48 object-contain hover:scale-105 transition-all duration-300"
                    preview="true"
                  />
                  <div class="px-2 text-xs text-center">{{ image.name }}</div>
                </div>
              </ng-template>
            </p-carousel>
          </div>
        }
      }

      @if (showAgents()) {
        <div class="flex flex-col gap-2">
          <div class="flex-shrink-0 font-bold mb-2 mt-2">Proposed AI Business Agents:</div>
          <div class="flex flex-auto flex-wrap flex-wrap gap-2">
            @for (agent of agents(); track $index) {
              <div
                class="p-2 drop-shadow-lg rounded-lg text-left text-xs bg-neutral-900 font-normal text-yellow-500 flex flex-col align-center justify-center gap-1"
              >
                <div class="flex flex-col font-semibold">
                  <div class="flex items-center justify-center text-md">
                    <i
                      class="ph ph-head-circuit text-yellow-500 bg-neutral-800 rounded-full p-1"
                    ></i>
                    <p class="font-bold">{{ agent?.agent }}</p>
                  </div>
                  <hr class="w-full bg-neutral-700 my-2" />
                  @if (agent?.description) {
                    <p class="text-neutral-300 mb-2">
                      Description:
                      <span class="font-light text-white italic">{{ agent?.description }} </span>
                    </p>
                  }
                  @if (agent?.benefits) {
                    <p class="text-neutral-300 mb-2">
                      Benefits:
                      <span class="font-light text-white italic">{{ agent?.benefits }} </span>
                    </p>
                  }
                  @if (agent?.data_model) {
                    <p class="text-neutral-300 mb-2">
                      Data Model:
                      <span class="font-light text-white italic">{{ agent?.data_model }}</span>
                    </p>
                  }
                  @if (agent?.nodes) {
                    <p class="text-neutral-300 mb-2">
                      Nodes will replace:
                      @for (node of agent?.nodes; track $index) {
                        <span
                          class="font-bold bg-yellow-500/20 rounded-lg px-1 py-1 text-xs/8 leading-2 text-white"
                          >{{ node }}</span
                        >
                        @if (!$last) {
                          <span>,</span>
                        }
                      }
                    </p>
                  }
                </div>
                <button
                  (click)="implementAgent(agent)"
                  class="text-white border border-yellow-500 rounded-md px-2 py-1 text-xs flex items-center gap-1 w-auto self-center hover:bg-yellow-500/20 cursor-pointer mb-2"
                >
                  <i class="ph ph-sparkle text-yellow-500"></i>
                  Implement
                </button>
              </div>
            }
          </div>
        </div>
      }

      @if (warnings().length) {
        <div class="flex flex-col gap-2 text-xs bg-yellow-500/20 rounded-lg p-2">
          @for (warning of warnings(); track $index) {
            <div class="text-yellow-300 flex flex-row justify-start items-start">
              <i
                class="ph ph-flag mr-2 flex items-center !w-5 !h-5 p-1 bg-yellow-800 rounded-full"
              ></i>
              <div
                [innerHTML]="warning.message | flxAiMarkdown | async"
                class="whitespace-normal break-normal"
              ></div>
            </div>
          }
        </div>
      }

      @if (changes()?.added?.length || changes()?.removed?.length) {
        <flx-ai-analyst-summary
          [added]="changes().added"
          [removed]="changes().removed"
        />
      }

      @if (hasDataModelChanges()) {
        <div class="flex flex-col bg-black/30 gap-1 py-2 px-3 rounded-lg">
          <div class="flex flex-col">
            @if (hasExistingDataModelChanges()) {
              <div class="text-green-300">
                <span class="font-bold">{{ dataModelChangesSummary().noExisting }}</span> existing
                data model keys matched:
              </div>

              <ng-container
                *ngTemplateOutlet="
                  dataModelTable;
                  context: { $implicit: dataModelChanges().existing }
                "
              />
            }

            <div
              class="w-full h-[0.5px] bg-neutral-700 my-2 last-of-type:hidden first-of-type:hidden"
            ></div>

            @if (hasNewDataModelChanges()) {
              <div class="text-yellow-300">
                <span class="font-bold">{{ dataModelChangesSummary().noNew }}</span> data model keys
                to be created:
              </div>

              <ng-container
                *ngTemplateOutlet="dataModelTable; context: { $implicit: dataModelChanges().new }"
              />
            }

            <div class="w-full h-[0.5px] bg-neutral-700 my-2"></div>

            <button
              [disabled]="!isMessageLast()"
              (click)="showDataModelModifyModal()"
              class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
            >
              Modify Keys
            </button>
          </div>
        </div>
      }

      @if (message().sources?.length) {
        <div class="flex gap-2 text-xs leading-4 font-semibold mt-4">
          <div class="flex-shrink-0">Sources:</div>
          <div class="flex flex-auto flex-wrap flex-wrap gap-2">
            @for (reference of message().sources; track reference.link) {
              <a
                [href]="reference.link"
                class="text-yellow-500"
                target="_blank"
              >
                <pre class="inline">[{{ $index + 1 }}]</pre>
                {{ reference.title }}</a
              >

              @if (!$last) {
                <span>/</span>
              }
            }
          </div>
        </div>
      }
      @if (message().objects?.length) {
        <div class="flex flex-col gap-2 text-xs leading-4 mt-4">
          <div class="flex-shrink-0 font-bold">References:</div>
          <div class="flex flex-auto flex-wrap flex-wrap gap-2">
            @for (item of message().objects; track $index) {
              <button
                (click)="objectSelected(item)"
                class="px-2 py-1 drop-shadow-lg text-left font-semibold rounded-lg border cursor-pointer border-yellow-500 flex-flex-row text-yellow-500"
              >
                <i [class]="getIconByType(item.object_type)"></i>
                {{ item.name }}
              </button>
            }
          </div>
        </div>
      }
      @if (message().html_preview && isMessageLast()) {
        <div class="flex flex-auto flex-wrap flex-wrap gap-2">
          <button
            (click)="showPreviewHtml()"
            class="px-4 py-2 drop-shadow-lg rounded-lg text-left bg-yellow-500 font-semibold cursor-pointer text-neutral-700"
          >
            Preview User Interface
          </button>
        </div>
      }
      @if (message().prompts?.length && isMessageLast()) {
        <div class="flex flex-col gap-2 text-xs leading-4 mt-4">
          <div class="flex-shrink-0 font-bold">
            {{ message().sources ? 'Expand the Discussion:' : 'Quick Start Ideas:' }}
          </div>
          <div class="flex flex-auto flex-wrap flex-wrap gap-2">
            @for (prompt of message().prompts; track $index) {
              <button
                (click)="promptSelected(prompt)"
                class="p-2 drop-shadow-lg rounded-lg text-left text-xs bg-neutral-900 hover:bg-neutral-800 font-normal cursor-pointer text-yellow-500 flex align-center justify-center gap-1"
              >
                <div class="flex items-center justify-center">
                  <i
                    [ngClass]="getIconByAgent(prompt?.handled_by)"
                    class="text-yellow-500 bg-neutral-800 rounded-full p-1"
                    pTooltip="Handled by {{ prompt?.handled_by?.toUpperCase() || 'ASSISTANT' }}"
                    tooltipPosition="top"
                  ></i>
                </div>
                <div class="flex flex-col font-semibold">
                  {{ prompt?.action }}
                  @if (prompt?.description) {
                    <p class="text-neutral-300 font-light">
                      {{ prompt?.description }}
                    </p>
                  }
                </div>
              </button>
            }
          </div>
        </div>
      }

      @if (message().generatedImage) {
        <div class="flex flex-col gap-2 mt-4">
          <div class="flex-shrink-0 font-bold">Generated Image:</div>
          <div class="flex flex-auto flex-wrap flex-wrap gap-2">
            <div class="w-[96px] h-[108px] overflow-hidden rounded-lg">
              <p-image
                [src]="message().generatedImage"
                [alt]="'generated_image'"
                class="object-cover"
                width="96px"
                preview="true"
                height="108px"
                imageClass="min-w-[96px] w-auto min-h-[108px] h-auto object-contain bg-white"
              />
            </div>
          </div>
        </div>
      }

      @if (hasMessageActions()) {
        <div class="flex flex-col gap-2 text-xs leading-4 mt-4">
          <div class="flex-shrink-0 font-bold">Other options:</div>
          <div class="flex flex-wrap gap-2">
            @if (showImageUploadAction()) {
              <button
                [disabled]="isUploadDisabled()"
                (click)="imageInput.click()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                From Image
              </button>

              <input
                #imageInput
                [accept]="allowedImageFileTypes()"
                [multiple]="isMultipleImageUploadAllowed()"
                (change)="onFileSelected($event)"
                class="hidden"
                type="file"
              />
            }

            @if (showBpmnUploadAction()) {
              <button
                [disabled]="isUploadDisabled()"
                (click)="bpmnInput.click()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                From BPMN
              </button>

              <input
                #bpmnInput
                [accept]="allowedBpmnFileTypes()"
                (change)="onFileSelected($event)"
                class="hidden"
                type="file"
              />
            }

            @if (showFromDocumentsAction()) {
              <button
                [disabled]="isUploadDisabled()"
                (click)="checkContextDocuments()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                From Documents
              </button>
            }

            @if (showProcessUploadAction()) {
              <button
                [disabled]="isUploadDisabled()"
                (click)="bpmnInput.click()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                From BPMN file
              </button>

              <input
                #bpmnInput
                [accept]="allowedProcessFileTypes()"
                [multiple]="isMultipleProcessUploadAllowed()"
                (change)="onFileSelected($event)"
                class="hidden"
                type="file"
              />
            }

            @if (showDocumentUploadAction()) {
              <button
                [disabled]="isUploadDisabled()"
                (click)="documentInput.click()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                Upload document
              </button>

              <input
                #documentInput
                [accept]="allowedDocumentFileTypes()"
                [multiple]="isMultipleDocumentUploadAllowed()"
                (change)="onFileSelected($event)"
                class="hidden"
                type="file"
              />
            }

            @if (showStartNewSessionAction()) {
              <button
                (click)="startNewSession()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                New session
              </button>
            }
            @if (showYesNoAction()) {
              <button
                (click)="yesAction()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                Yes
              </button>
              <button
                (click)="noAction()"
                class="border border-neutral-400 rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                No
              </button>
            }
          </div>
        </div>
      }

      @if (showYesNoAction() && isMessageLast()) {
        <div class="flex flex-col gap-2 text-xs leading-4 mt-2">
          <div class="flex flex-wrap gap-2">
            <button
              (click)="yesAction()"
              class="bg-green-400 text-white rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed hover:bg-green-800"
            >
              Yes
            </button>
            <button
              (click)="noAction()"
              class="bg-red-400 text-white rounded-full px-3 py-1 text-xs disabled:text-neutral-400 disabled:cursor-not-allowed hover:bg-red-800"
            >
              No
            </button>
          </div>
        </div>
      }

      @if (interruptOptions()?.length && isMessageLast()) {
        <div class="flex flex-col gap-2 text-xs leading-4 mt-2">
          @for (option of interruptOptions(); track option.value) {
            <div
              (click)="selectInterruptOption(option)"
              (keydown.enter)="selectInterruptOption(option)"
              class="flex items-center gap-2 px-3 py-2 bg-neutral-800 rounded-lg cursor-pointer
                     hover:bg-neutral-700 border border-neutral-600 hover:border-yellow-500 transition-colors"
              tabindex="0"
            >
              <i class="ph ph-caret-right text-yellow-500 text-sm"></i>
              <span class="text-white">{{ option.label }}</span>
            </div>
          }
        </div>
      }

      @if (showFeedback()) {
        <div class="flex gap-2 mt-2">
          <button
            [ngClass]="{
              'border-neutral-700': !isFeedbackType()[FeedbackType.NEGATIVE],
              'border-red-400': isFeedbackType()[FeedbackType.NEGATIVE],
              'cursor-pointer': canSendFeedback(),
            }"
            (click)="submitFeedback(FeedbackType.NEGATIVE)"
            class="group w-7 h-7 flex justify-center items-center border rounded"
            tabindex="0"
          >
            <flx-icon
              [ngClass]="{
                'text-neutral-400': !isFeedbackType()[FeedbackType.NEGATIVE],
                'group-hover:text-neutral-50': canSendFeedback(),
                'text-red-400': isFeedbackType()[FeedbackType.NEGATIVE],
              }"
              name="thumbs-down"
            />
          </button>
          <button
            [ngClass]="{
              'border-neutral-700': !isFeedbackType()[FeedbackType.POSITIVE],
              'border-green-400': isFeedbackType()[FeedbackType.POSITIVE],
              'cursor-pointer': canSendFeedback(),
            }"
            (click)="submitFeedback(FeedbackType.POSITIVE)"
            class="group w-7 h-7 flex justify-center items-center border rounded"
            tabindex="1"
          >
            <flx-icon
              [ngClass]="{
                'text-neutral-400': !isFeedbackType()[FeedbackType.POSITIVE],
                'group-hover:text-neutral-50': canSendFeedback(),
                'text-green-400': isFeedbackType()[FeedbackType.POSITIVE],
              }"
              name="thumbs-up"
            />
          </button>
        </div>
      }
    </div>
  </div>
}

<ng-template
  #dataModelTable
  let-items
>
  @for (dataModel of items; track $index) {
    @if (!dataModel.inactive) {
      <div class="flex flex-row gap-3 text-xs/[18px] even:bg-white/5 whitespace-normal py-[2px]">
        <div class="flex font-semibold text-white w-24 grow-0 shrink-0 items-center justify-start">
          {{ dataModel.label }}
        </div>
        <div class="flex text-neutral-200 flex-grow items-center justify-end">
          {{ dataModel.key }}
        </div>
      </div>
    }
  }
</ng-template>

<ng-template
  #msg
  let-message
>
  <div
    #streamContainer
    [class.text-orange-500]="isErrorMessage()"
    [innerHTML]="message | flxAiMarkdown | async | highlightBracketed"
    [class.streaming]="
      streamingAiResponse() && isMessageLast() && !isErrorMessage() && !isUserMessage()
    "
    class="streamed-text markdown leading-6 whitespace-normal flex flex-col gap-2"
  ></div>
</ng-template>

<ng-template [flxShowModal]="previewHtmlVisible()">
  <flx-modal
    [dismissible]="true"
    (dismiss)="closePreviewHtml()"
    class="flex flex-col w-[80vw] min-h-[80vh] bg-neutral-900 preview-html-modal"
  >
    <div
      class="flx-modal-title text-white"
      flxModalHeaderTitle
    >
      Preview User Interface
    </div>
    <div
      [innerHTML]="getSafeHtml()"
      class="flex flex-col bg-white overflow-y-auto h-full w-full select-none"
    ></div>
  </flx-modal>
</ng-template>
